// src/pages/Settings/components/SettingsPanel.jsx
//
// The shared header for a settings panel.
//
// Before this, ~45 panels each copy-pasted `<h3 className="p-2">X Settings</h3>`
// followed by a reactstrap Nav strip and an <h5> that just echoed the active
// tab's name. This replaces all three: the title carries the panel name, the
// segmented control carries the tabs, and the echo is redundant once the active
// pill is visible.
//
// Tabs are optional. Panels with a single tab (quoting, banking, timeEntry,
// SalesOrder, purchaseOrder, …) should pass no `tabs` at all rather than render
// a one-item strip.
//
// See apps/web/SETTINGS_UNIFORMITY.md.

import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";

import PageShell from "../../../Components/PageShell/PageShell";
import { SettingsSubTabContext } from "../settingsTabContext";
import "./settingsPanel.styles.scss";

// Human-readable URL segment for a tab. A tab may carry an explicit `slug`
// (e.g. "year-end"); otherwise we derive one from its string `label`
// ("Year End Periods" -> "year-end-periods"). Non-string labels (React nodes)
// fall back to the raw `value`, so a slug is always defined.
const tabSlug = (tab) => {
  if (tab.slug) return String(tab.slug).toLowerCase();
  if (typeof tab.label === "string" || typeof tab.label === "number") {
    const derived = String(tab.label)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (derived) return derived;
  }
  return String(tab.value).toLowerCase();
};

const SettingsPanel = ({
  title,
  description,
  actions,
  tabs = [],
  value,
  onChange,
  children,
  className = "",
}) => {
  // Deep-link bridge: the :activeTab URL segment lives on Settings/index.js and
  // is handed down through context. The parent panel still owns its tab state
  // (value/onChange); we only sync it to/from the URL so a panel can be opened
  // straight on a given tab (e.g. /settings/1/24/year-end-periods) and tab
  // clicks stay shareable/bookmarkable. Panels rendered outside the settings
  // frame get a null context and behave exactly as before.
  const subTabCtx = useContext(SettingsSubTabContext);
  const subTab = subTabCtx?.subTab ?? null;
  const setSubTab = subTabCtx?.setSubTab;

  useEffect(() => {
    if (subTab == null || !onChange || tabs.length <= 1) return;
    const wanted = String(subTab).toLowerCase();
    // Match the URL segment against the tab's slug first, then fall back to its
    // raw `value` so older numeric deep links (e.g. /settings/1/24/3) keep
    // working. Only nudge the parent when it points at a real, non-active tab —
    // a user's own click sets subTab === slug, so this never fights that.
    const match = tabs.find(
      (tab) => tabSlug(tab) === wanted || String(tab.value).toLowerCase() === wanted
    );
    if (match && String(match.value) !== String(value)) {
      onChange(match.value);
    }
    // Intentionally keyed on subTab only: re-run when the URL changes, not on
    // every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  return (
    <section className={`settings-panel ${className}`.trim()}>
      {/* The compact PageShell hero, so a settings panel and a list page wear
          the same header. Never pass `stats` — the panel lives inside the
          settings frame's viewport-locked pane. */}
      <PageShell
        compact
        className="settings-panel__shell"
        title={title}
        description={description}
        actions={actions}
      />

      {tabs.length > 1 && (
        <div className="settings-panel__tabs-wrap">
          <div className="settings-panel__tabs" role="tablist">
            {tabs.map((tab) => {
              const active = String(value) === String(tab.value);

              return (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={active ? "is-active" : ""}
                  onClick={() => {
                    onChange?.(tab.value);
                    // Reflect the choice in the URL as a readable slug so the
                    // tab is deep-linkable (e.g. .../year-end-periods).
                    setSubTab?.(tabSlug(tab));
                  }}
                >
                  {tab.icon && (
                    <i className={`${tab.icon} settings-panel__tab-icon`} />
                  )}
                  {tab.label}
                  {tab.count !== undefined && <em>{tab.count}</em>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="settings-panel__body">{children}</div>
    </section>
  );
};

SettingsPanel.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  actions: PropTypes.node,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
      // Optional readable URL segment for this tab. Defaults to a slug derived
      // from `label` (falling back to `value`).
      slug: PropTypes.string,
      icon: PropTypes.string,
      count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default SettingsPanel;
